import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  Button,
  CopyLinkButton,
  EmptyState,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  LinkButton,
  Modal,
  cx,
} from "@/components/ui";
import { DataFreshness, freshnessOf } from "@/components/ui/DataFreshness";
import { FeatureTip } from "@/components/whats-new/FeatureTip";
import { portal } from "@/lib/api";
import { config } from "@/lib/config";
import { downloadBlob } from "@/lib/download";
import { useHosting } from "@/lib/hosting";
import { primarySite, siteKeys, useOverview } from "@/lib/queries";
import type { AnalyticsDay, SiteAnalytics } from "@/lib/types";
import { choice, day, text, useUrlView } from "@/lib/url-state";

type Period = 7 | 30 | 90 | "custom";
/**
 * The view kept in the URL (PHP-3341): `?site=` (default the workspace's main website), `?period=`
 * 7, 30, 90 or custom (default 30 days, or 7 where the plan keeps less) and, for custom, `?from=` and
 * `?to=` (YYYY-MM-DD). A site that isn't there, or a period the plan doesn't include, reads as the default.
 */
const VIEW = {
  site: text("site"),
  period: choice<"" | "7" | "30" | "90" | "custom">("period", ["7", "30", "90", "custom"], ""),
  from: day("from"),
  to: day("to"),
};
const SOURCE_LABEL = { form: "Contact form", whatsapp: "WhatsApp", phone: "Phone", email: "Email" } as const;
/** Enough enquiries to say where they come from. */
const ATTRIBUTION_MIN = 5;
/** A page needs this many views before its missing enquiries say anything. */
const INSIGHT_MIN_VIEWS = 20;

/** A Figma glyph (public/icons/analytics), centred at its drawn size in a `box`px slot. */
function Glyph({ name, box, className }: { name: string; box: number; className?: string }) {
  return (
    <span
      className={cx("inline-flex shrink-0 items-center justify-center", className)}
      style={{ width: box, height: box }}
    >
      <img src={`/icons/analytics/${name}.svg`} alt="" className="block max-w-none" />
    </span>
  );
}

const sum = (days: AnalyticsDay[], k: keyof Omit<AnalyticsDay, "day">) => days.reduce((n, d) => n + d[k], 0);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "21 Aug" (a `YYYY-MM-DD` day, read as the UTC day it is). */
const shortDay = (day: string) => {
  const d = new Date(`${day}T00:00:00Z`);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
function pageName(p: { path: string; title: string | null }): string {
  if (p.title) return p.title.split(/\s[|·–—-]\s/)[0] ?? p.title;
  if (p.path === "/") return "Home";
  const last = p.path.replace(/\/+$/, "").split("/").at(-1) ?? p.path;
  const words = decodeURIComponent(last)
    .replace(/\.html?$/, "")
    .replace(/[-_]+/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Figma "Analytics — Overview · polished". */
export function AnalyticsPage() {
  const { orgId } = useOrganization();
  const overview = useOverview();
  const hosting = useHosting();
  const [view, setView] = useUrlView(VIEW);
  const [customOpen, setCustomOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);

  const sites = hosting.data ?? [];
  const site =
    sites.find((h) => h.site.id === view.site) ??
    sites.find((h) => h.site.id === primarySite(sites.map((s) => s.site))?.id) ??
    sites[0];
  const plan = overview.data?.account.plan;
  const history = plan?.analytics_history_days ?? null;
  const allowed = (days: number) => history === null || days <= history;
  const custom = view.from && view.to && view.from <= view.to ? { from: view.from, to: view.to } : null;
  const asked: Period | null =
    view.period === "custom"
      ? custom && "custom"
      : view.period && allowed(Number(view.period))
        ? (Number(view.period) as 7 | 30 | 90)
        : null;
  const active: Period = asked ?? (allowed(30) ? 30 : 7);
  const range = active === "custom" && custom ? custom : { days: active === "custom" ? 30 : active };

  const analytics = useQuery({
    queryKey: siteKeys.analytics(orgId, site?.site.id ?? "", range),
    queryFn: () => portal.siteAnalytics(site?.site.id ?? "", range),
    enabled: Boolean(site && plan),
    refetchInterval: 5 * 60_000,
    placeholderData: (prev) => prev,
  });

  if (overview.isPending || hosting.isPending) return <FullPageSpinner />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  if (hosting.isError) return <ErrorState error={hosting.error} onRetry={() => void hosting.refetch()} />;
  if (!site)
    return (
      <EmptyState
        icon={<Glyph name="analytics-head" box={20} />}
        title="Nothing to measure yet"
        description="Publish your website and its visitors start showing up here."
        action={
          <Link to="/websites" className="text-sm font-semibold text-brand-700 hover:underline">
            Go to Website →
          </Link>
        }
      />
    );

  const address = site.domains.find((d) => d.status === "active")?.hostname ?? site.site.hostname;
  const a = analytics.data;
  const span = a ? a.days.length : 0;
  const rangeLabel =
    active === "custom" && a
      ? `${shortDay(a.from_day)} – ${shortDay(a.to_day)}`
      : `Last ${span || ("days" in range ? range.days : 30)} days`;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start gap-2.5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted shadow-[0_2px_5px_rgba(0,0,0,0.05)]">
          <Glyph name="analytics-head" box={20} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
          <h1 className="truncate text-[28px] font-semibold tracking-[-0.3px] text-fg">
            How {address} performed
          </h1>
          <p className="text-sm text-fg-subtle">
            {rangeLabel}
            {a?.comparison_available ? ` vs the ${span} days before` : ""} · refreshes every few minutes
          </p>
          <DataFreshness {...freshnessOf(analytics)} />
        </div>
        {sites.length > 1 && (
          <label className="relative flex items-center print:hidden">
            <Glyph name="browser-filter" box={14} className="pointer-events-none absolute left-3.5" />
            <select
              aria-label="Site"
              value={site.site.id}
              onChange={(e) => setView({ site: e.target.value })}
              className="appearance-none rounded-full border border-border bg-surface py-2.5 pr-9 pl-[34px] text-[13px] font-semibold text-fg outline-none focus:border-brand-500"
            >
              {sites.map((h) => (
                <option key={h.site.id} value={h.site.id}>
                  {h.domains.find((d) => d.status === "active")?.hostname ?? h.site.hostname}
                </option>
              ))}
            </select>
            <Glyph name="chevron-down" box={15} className="pointer-events-none absolute right-3" />
          </label>
        )}
        <CopyLinkButton className="py-2.5 print:hidden" />
        <div className="relative print:hidden">
          <div
            className="flex items-center gap-0.5 rounded-[11px] bg-[#faf6ef] p-1"
            role="tablist"
            aria-label="Period"
          >
            {([7, 30, 90, "custom"] as const).map((p) => {
              const locked = p !== "custom" && !allowed(p);
              const on = active === p;
              return (
                <Button
                  key={p}
                  unstyled
                  role="tab"
                  aria-selected={on}
                  disabledReason={
                    locked ? `Included on paid plans (${plan?.name} shows ${history} days).` : undefined
                  }
                  onClick={() =>
                    p === "custom"
                      ? setCustomOpen((o) => !o)
                      : (setView({ period: String(p) as "7" | "30" | "90", from: null, to: null }),
                        setCustomOpen(false))
                  }
                  className={cx(
                    "rounded-lg px-3.5 py-2 text-[13px]",
                    on
                      ? "bg-surface font-semibold text-fg drop-shadow-[0_1px_1.5px_rgba(26,20,18,0.06)]"
                      : "font-medium text-[#766e6a] hover:text-fg disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {p === "custom" ? "Custom" : `${p}d`}
                </Button>
              );
            })}
          </div>
          {customOpen && (
            <CustomRange
              history={history}
              initial={custom}
              onApply={(r) => {
                setView({ period: "custom", from: r.from, to: r.to });
                setCustomOpen(false);
              }}
              onClose={() => setCustomOpen(false)}
            />
          )}
        </div>
      </div>

      {history !== null && (
        <p className="-mt-3 text-[12.5px] text-fg-muted print:hidden">
          Your {plan?.name} plan shows the last {history} days.{" "}
          <Link to="/billing/plans" className="font-semibold text-brand-500 hover:underline">
            Compare plans →
          </Link>
        </p>
      )}

      <FeatureTip placement="analytics" />

      {analytics.isError ? (
        <ErrorState error={analytics.error} onRetry={() => void analytics.refetch()} />
      ) : !a ? (
        <FullPageSpinner />
      ) : (
        <Report
          a={a}
          siteId={site.site.id}
          builder={site.site.kind !== "external"}
          onHow={() => setHowOpen(true)}
          rangeLabel={rangeLabel}
        />
      )}

      {howOpen && <HowWeCount onClose={() => setHowOpen(false)} />}
    </div>
  );
}

function Report({
  a,
  siteId,
  builder,
  rangeLabel,
  onHow,
}: {
  a: SiteAnalytics;
  siteId: string;
  builder: boolean;
  rangeLabel: string;
  onHow: () => void;
}) {
  const visitors = sum(a.days, "visitors");
  const views = sum(a.days, "page_views");
  const enquiries = sum(a.days, "enquiries");
  // Nothing recorded before this period (or the plan's history stops here): nothing to compare.
  const hadEarlier = a.comparison_available && a.previous_days.some((d) => d.visitors > 0);
  const prev = hadEarlier
    ? {
        visitors: sum(a.previous_days, "visitors"),
        views: sum(a.previous_days, "page_views"),
        enquiries: sum(a.previous_days, "enquiries"),
      }
    : null;
  const rate = visitors ? (enquiries / visitors) * 100 : 0;
  const prevRate = prev?.visitors ? (prev.enquiries / prev.visitors) * 100 : 0;
  const perVisitor = visitors ? views / visitors : 0;
  const prevPerVisitor = prev?.visitors ? prev.views / prev.visitors : 0;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Visitors"
          value={visitors.toLocaleString()}
          delta={
            prev &&
            (prev.visitors
              ? delta((visitors - prev.visitors) / prev.visitors, (v) => `${Math.round(Math.abs(v) * 100)}%`)
              : null)
          }
        />
        <Stat
          label="Enquiries"
          value={enquiries.toLocaleString()}
          delta={prev && delta(enquiries - prev.enquiries, (v) => Math.abs(v).toLocaleString())}
        />
        <Stat
          label="Enquiry rate"
          value={`${rate.toFixed(1)}%`}
          delta={prev && prev.visitors ? delta(rate - prevRate, (v) => `${Math.abs(v).toFixed(1)}pt`) : null}
        />
        <Stat
          label="Pages per visitor"
          value={perVisitor.toFixed(1)}
          delta={
            prev && prev.visitors ? delta(perVisitor - prevPerVisitor, (v) => Math.abs(v).toFixed(1)) : null
          }
        />
      </div>

      <div className="flex items-center gap-2.5 rounded-xl border border-border bg-brand-50 px-3.5 py-2.5">
        <Glyph name="info" box={14} />
        <p className="min-w-0 flex-1 text-[12.5px] leading-normal text-fg-muted">
          Visitors are unique people each day, counted without cookies · Enquiries are contact-form sends and
          WhatsApp, phone or email taps · Enquiry rate is enquiries ÷ visitors. Browsers that ask not to be
          tracked aren’t counted.
        </p>
        <button
          type="button"
          onClick={onHow}
          className="shrink-0 text-[12.5px] font-semibold text-brand-500 hover:underline print:hidden"
        >
          How we count →
        </button>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          <VisitorsChart
            a={a}
            rangeLabel={rangeLabel}
            visitors={visitors}
            previous={prev?.visitors ?? null}
          />
          <TopPages a={a} />
        </div>
        <div className="flex w-full shrink-0 flex-col gap-5 lg:w-[344px]">
          <Insight a={a} siteId={siteId} builder={builder} />
          <Attribution a={a} enquiries={enquiries} />
          <Export a={a} />
        </div>
      </div>
    </>
  );
}

type Delta = { text: string; dir: "up" | "down" | "flat" };
function delta(value: number, fmt: (v: number) => string): Delta {
  const dir = Math.abs(value) < 1e-9 ? "flat" : value > 0 ? "up" : "down";
  return { text: dir === "flat" ? "Same as prior" : `${fmt(value)} vs prior`, dir };
}

function Stat({ label, value, delta: d }: { label: string; value: string; delta: Delta | null }) {
  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-border bg-surface p-[18px] drop-shadow-[0_4px_7px_rgba(26,20,18,0.05)]">
      <p className="text-[13px] font-medium text-[#766e6a]">{label}</p>
      <p className="text-[34px] leading-none font-semibold text-fg">{value}</p>
      {d ? (
        <span
          className={cx(
            "flex items-center gap-1.5 self-start rounded-full py-1 pr-[9px] pl-2 text-xs font-semibold",
            d.dir === "up"
              ? "bg-[#eef8f3] text-[#007955]"
              : d.dir === "down"
                ? "bg-[#fff1ee] text-[#b52522]"
                : "bg-muted text-fg-muted",
          )}
        >
          {d.dir === "up" && (
            <>
              <img src="/icons/analytics/dot-up.svg" alt="" className="size-[7px]" />
              <Glyph name="arrow-up" box={13} />
            </>
          )}
          {d.dir === "down" && (
            <>
              <img src="/icons/analytics/dot-down.svg" alt="" className="size-[7px]" />
              <Glyph name="arrow-down" box={13} />
            </>
          )}
          {d.text}
        </span>
      ) : (
        <span className="self-start rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-fg-muted">
          No earlier data
        </span>
      )}
    </div>
  );
}

const card =
  "flex flex-col gap-4 rounded-[18px] border border-border bg-surface p-[22px] drop-shadow-[0_6px_10px_rgba(26,20,18,0.08)]";

function VisitorsChart({
  a,
  rangeLabel,
  visitors,
  previous,
}: {
  a: SiteAnalytics;
  rangeLabel: string;
  visitors: number;
  previous: number | null;
}) {
  const days = a.days;
  const prev = a.comparison_available && a.previous_days.some((d) => d.visitors > 0) ? a.previous_days : [];
  const max = Math.max(1, ...days.map((d) => d.visitors), ...prev.map((d) => d.visitors));
  const recent = days.length > 7 ? days.length - 7 : 0;
  const trend =
    previous === null
      ? "Daily visitors over the period"
      : visitors > previous
        ? "Trending upward compared to the previous period"
        : visitors < previous
          ? "Trending downward compared to the previous period"
          : "Level with the previous period";
  // The previous period as a line over the bars: one point per bar centre.
  const points = prev
    .map((d, i) => `${((i + 0.5) / prev.length) * 100},${100 - (d.visitors / max) * 100}`)
    .join(" ");
  return (
    <section className={card}>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex flex-col gap-[3px]">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <Glyph name="analytics" box={15} />
            Visitors, {rangeLabel.charAt(0).toLowerCase() + rangeLabel.slice(1)}
          </h2>
          <p className="text-sm text-fg-subtle">{trend}</p>
        </div>
        <span className="flex-1" />
        <div className="flex items-center gap-4 text-[13px] font-medium text-[#766e6a]">
          <span className="flex items-center gap-[7px]">
            <img src="/icons/analytics/legend-current.svg" alt="" className="size-[9px]" />
            Visitors
          </span>
          {prev.length > 0 && (
            <span className="flex items-center gap-[7px]">
              <img src="/icons/analytics/legend-previous.svg" alt="" className="size-[9px]" />
              Previous period
            </span>
          )}
        </div>
      </div>
      {visitors === 0 && !a.collecting_since ? (
        <div className="flex h-40 items-center justify-center rounded-xl bg-[#faf6ef] px-6 text-center text-sm text-fg-muted">
          No visits recorded yet. Counting started when analytics was switched on: share your site’s address
          and visits appear here within minutes.
        </div>
      ) : (
        <div className="relative h-40 border-b border-border">
          <div className="flex h-full items-end gap-[3px] sm:gap-1.5">
            {days.map((d, i) => (
              <div
                key={d.day}
                title={`${shortDay(d.day)} · ${d.visitors.toLocaleString()} visitors · ${d.page_views.toLocaleString()} views`}
                className={cx(
                  "min-w-0 flex-1 rounded-t-[3px]",
                  i >= recent ? "bg-brand-500" : "bg-[#f8b6a0]",
                )}
                style={{ height: `${(d.visitors / max) * 100}%`, minHeight: d.visitors ? 2 : 0 }}
              />
            ))}
          </div>
          {points && (
            <svg
              className="pointer-events-none absolute inset-0 size-full overflow-visible"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              <polyline
                points={points}
                fill="none"
                stroke="black"
                strokeWidth="1.5"
                strokeDasharray="4 4"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          )}
        </div>
      )}
      <div className="flex text-xs text-fg-subtle">
        <span>{shortDay(a.from_day)}</span>
        <span className="flex-1" />
        <span>{shortDay(a.to_day)}</span>
      </div>
    </section>
  );
}

function TopPages({ a }: { a: SiteAnalytics }) {
  const pages = a.top_pages.slice(0, 5);
  return (
    <section className={card}>
      <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
        <Glyph name="browser" box={15} />
        Top pages
      </h2>
      {pages.length === 0 ? (
        <p className="text-sm text-fg-subtle">No pages were viewed in this period.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex gap-3.5 border-b border-border pb-2.5 text-xs font-semibold text-fg-subtle">
            <span className="min-w-0 flex-1 sm:w-[280px] sm:flex-none">Page</span>
            <span className="w-[72px] sm:w-[100px]">Views</span>
            <span className="w-[72px] sm:w-[100px]">Enquiries</span>
          </div>
          {pages.map((p, i) => (
            <div key={p.path} className={cx("flex gap-3.5 text-sm", i > 0 && "border-t border-border pt-3")}>
              <span
                className="min-w-0 flex-1 truncate font-semibold text-fg sm:w-[280px] sm:flex-none"
                title={p.path}
              >
                {pageName(p)}
              </span>
              <span className="w-[72px] text-fg-muted sm:w-[100px]">{p.page_views.toLocaleString()}</span>
              <span className="w-[72px] text-fg-muted sm:w-[100px]">{p.enquiries.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** A plain observation from the numbers: a well-visited page that never leads to an enquiry. */
function Insight({ a, siteId, builder }: { a: SiteAnalytics; siteId: string; builder: boolean }) {
  const page = a.top_pages.find(
    (p) => p.page_views >= INSIGHT_MIN_VIEWS && p.enquiries === 0 && p.path !== "/contact",
  );
  const key = page ? `cg.analytics.insight.${siteId}.${page.path}` : "";
  const [hidden, setHidden] = useState(() => {
    try {
      return key ? localStorage.getItem(key) === "dismissed" : false;
    } catch {
      return false;
    }
  });
  if (!page || hidden) return null;
  const dismiss = () => {
    try {
      localStorage.setItem(key, "dismissed");
    } catch {
      /* per-browser convenience only */
    }
    setHidden(true);
  };
  return (
    <section
      className="flex flex-col gap-3.5 rounded-[18px] p-[22px] text-white drop-shadow-[0_8px_12px_rgba(26,20,18,0.18)] print:hidden"
      style={{
        backgroundImage: "linear-gradient(149.36deg, rgb(92, 23, 11) 0.36%, rgb(242, 78, 41) 98.77%)",
      }}
    >
      <div className="flex items-center gap-2.5">
        <span className="flex w-8 items-center justify-center rounded-[10px] bg-white/16">
          <span className="flex size-7 items-center justify-center rounded-full border border-brand-100 bg-brand-50">
            <Glyph name="weavex" box={15} />
          </span>
        </span>
        <h2 className="text-base font-semibold">Weavex noticed something</h2>
      </div>
      <p className="text-sm">
        Visitors opened your {pageName(page)} page {page.page_views.toLocaleString()} times in this period,
        but none of those visits led to an enquiry. A clear way to get in touch on that page could turn more
        of that interest into enquiries.
      </p>
      <div className="flex flex-wrap items-center gap-[9px]">
        {builder && (
          <LinkButton
            to={`${config.builderUrl}/sites/${siteId}`}
            requires={{ permission: "websites:update", on: ["site", siteId] }}
            className="rounded-[10px] bg-white px-3.5 py-2.5 text-sm font-semibold text-brand-700 hover:bg-brand-50"
          >
            Edit with Weavex
          </LinkButton>
        )}
        <button
          type="button"
          onClick={dismiss}
          className="rounded-[10px] bg-white/12 px-3.5 py-2.5 text-sm font-semibold hover:bg-white/20"
        >
          Not now
        </button>
      </div>
    </section>
  );
}

function Attribution({ a, enquiries }: { a: SiteAnalytics; enquiries: number }) {
  return (
    <section className="flex flex-col gap-2.5 rounded-2xl bg-muted p-[18px]">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-fg">
        <Glyph name="comment" box={15} />
        Enquiry attribution
      </h2>
      {enquiries < ATTRIBUTION_MIN ? (
        <>
          <p className="text-[13px] text-[#766e6a]">
            Collecting data. Check back in a few days once we have enough activity to show a trend.
          </p>
          <span className="flex items-center gap-2 self-start rounded-full py-1 pr-[9px] pl-2 text-xs font-semibold text-fg-muted">
            <img src="/icons/analytics/dot-neutral.svg" alt="" className="size-[7px]" />
            <Glyph name="clock" box={13} />
            Collecting
          </span>
        </>
      ) : (
        <div className="flex flex-col gap-2.5">
          {a.enquiry_sources.map((s) => (
            <div key={s.source} className="flex flex-col gap-1">
              <div className="flex text-[13px]">
                <span className="font-semibold text-fg">{SOURCE_LABEL[s.source]}</span>
                <span className="flex-1" />
                <span className="text-fg-muted">
                  {s.enquiries.toLocaleString()} · {Math.round((s.enquiries / enquiries) * 100)}%
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-surface">
                <div
                  className="h-full rounded-full bg-brand-500"
                  style={{ width: `${(s.enquiries / enquiries) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Export({ a }: { a: SiteAnalytics }) {
  const [open, setOpen] = useState(false);
  const csv = useMutation({
    mutationFn: () => portal.analyticsExport(a.site_id, { from: a.from_day, to: a.to_day }),
    onSuccess: (blob) => downloadBlob(blob, `analytics-${a.from_day}-to-${a.to_day}.csv`),
  });
  return (
    <section className={cx(card, "print:hidden")}>
      <h2 className="flex items-center gap-2 text-base font-semibold text-fg">
        <Glyph name="copy" box={15} />
        Export report
      </h2>
      {a.exports_allowed ? (
        <>
          <p className="text-[13px] text-fg-subtle">
            Download this period as a PDF or CSV to share with your team.
          </p>
          {csv.isError && <ErrorAlert error={csv.error} />}
          <div className="relative self-start">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              className="rounded-[10px] border border-border bg-surface px-4 py-2.5 text-sm font-semibold text-fg hover:bg-muted"
            >
              Download report
            </button>
            {open && (
              <div
                role="menu"
                className="absolute left-0 z-20 mt-1.5 w-48 rounded-xl border border-border bg-surface p-1.5 shadow-lg"
              >
                <MenuItem
                  busy={csv.isPending}
                  onClick={() => {
                    setOpen(false);
                    csv.mutate();
                  }}
                >
                  {csv.isPending ? "Preparing CSV…" : "CSV spreadsheet"}
                </MenuItem>
                <MenuItem onClick={() => (setOpen(false), setTimeout(() => window.print(), 50))}>
                  PDF (print)
                </MenuItem>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="text-[13px] text-fg-subtle">
            Downloading reports as a PDF or CSV is included on the Pro plan.
          </p>
          <Link
            to="/billing/plans"
            className="self-start rounded-[10px] border border-border bg-surface px-4 py-2.5 text-sm font-semibold text-fg hover:bg-muted"
          >
            Compare plans
          </Link>
        </>
      )}
    </section>
  );
}

function MenuItem({
  onClick,
  children,
  busy = false,
}: {
  onClick: () => void;
  children: ReactNode;
  /** In progress: unavailable, and its label says what it is doing. */
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={busy}
      aria-busy={busy}
      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-fg hover:bg-muted disabled:opacity-60"
    >
      {children}
    </button>
  );
}

function CustomRange({
  history,
  initial,
  onApply,
  onClose,
}: {
  history: number | null;
  initial: { from: string; to: string } | null;
  onApply: (r: { from: string; to: string }) => void;
  onClose: () => void;
}) {
  // The calendar's bounds, fixed when the picker opens.
  const [{ today, earliest }] = useState(() => {
    const now = Date.now();
    return {
      today: isoDay(new Date(now)),
      earliest: isoDay(new Date(now - ((history ?? 400) - 1) * 86_400_000)),
    };
  });
  const [from, setFrom] = useState(() => {
    const twoWeeksAgo = isoDay(new Date(Date.parse(`${today}T00:00:00Z`) - 13 * 86_400_000));
    return initial?.from ?? (twoWeeksAgo < earliest ? earliest : twoWeeksAgo);
  });
  const [to, setTo] = useState(initial?.to ?? today);
  const valid = from <= to && from >= earliest && to <= today;
  return (
    <div className="absolute right-0 z-20 mt-1.5 flex w-72 flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-lg">
      <label className="flex flex-col gap-1 text-xs font-semibold text-fg-muted">
        From
        <input
          type="date"
          value={from}
          min={earliest}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
          className="rounded-lg border border-border px-2.5 py-2 text-sm font-normal text-fg"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-semibold text-fg-muted">
        To
        <input
          type="date"
          value={to}
          min={from}
          max={today}
          onChange={(e) => setTo(e.target.value)}
          className="rounded-lg border border-border px-2.5 py-2 text-sm font-normal text-fg"
        />
      </label>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-border px-3 py-1.5 text-sm font-semibold text-fg-muted hover:bg-muted"
        >
          Cancel
        </button>
        <Button
          unstyled
          disabledReason={
            valid
              ? undefined
              : `Choose a From date on or before the To date, between ${earliest} and ${today}.`
          }
          onClick={() => onApply({ from, to })}
          className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
        >
          Show range
        </Button>
      </div>
    </div>
  );
}

function HowWeCount({ onClose }: { onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title="How we count">
      <div className="flex flex-col gap-3 text-sm text-fg-muted">
        <p>
          Every page of your published site loads a tiny CortexGrip script from your own address. It sends a
          page view when a page opens, and an enquiry when someone sends a form or taps a WhatsApp, phone or
          email link.
        </p>
        <p>
          <span className="font-semibold text-fg">No cookies, nothing stored on the visitor’s device.</span> A
          visitor is recognised only within one day, from an anonymous code made from their connection and
          browser that changes every day and can’t be traced back to them. So the same person on two days
          counts as two visitors.
        </p>
        <p>
          Search engines, link previews and other bots aren’t counted, and neither are browsers set to “Do Not
          Track” or Global Privacy Control. Numbers update within minutes.
        </p>
      </div>
    </Modal>
  );
}
